import { CircleHelp } from 'lucide-react';

import { useTranslation } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';

import { isFreshStatus } from '../engine/status';
import { tourTitleKey } from '../registry';
import { useTourOptional } from '../tour-context';
import { useRouteTours } from '../use-route-tours';

function NewDot() {
  return <span aria-hidden className="bg-primary absolute top-1 right-1 size-2 rounded-full" />;
}

/**
 * "Take the tour" for the current route. One tour starts directly; a page with chapters opens a
 * menu of them. Renders nothing when the route has no tour the person can take.
 */
export function TourButton() {
  const tour = useTourOptional();
  return tour?.enabled ? <RouteTourButton /> : null;
}

// Split so pages rendered without a TourProvider (stories, unit tests) never touch the router.
function RouteTourButton() {
  const tour = useTourOptional();
  const tours = useRouteTours();
  const { t } = useTranslation('common');
  const { t: tt } = useTranslation(tour?.namespace ?? 'tours');
  const first = tours[0];
  if (!tour || !first) return null;
  const fresh = tours.some((definition) => isFreshStatus(tour.statusOf(definition)));
  const label = fresh ? t('tour.takeNew') : t('tour.take');

  const trigger = (onClick?: () => void) => (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="text-muted-foreground relative h-8 gap-1.5 px-2"
      aria-label={label}
      data-tour-button
      onClick={onClick}
    >
      <CircleHelp aria-hidden />
      <span className="hidden md:inline">{t('tour.take')}</span>
      {fresh ? <NewDot /> : null}
    </Button>
  );

  if (tours.length === 1) {
    return trigger(() => void tour.start(first.id, { source: 'page-button' }));
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger()}</DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuLabel>{t('tour.chapters')}</DropdownMenuLabel>
        {tours.map((definition) => (
          <DropdownMenuItem
            key={definition.id}
            onSelect={() => void tour.start(definition.id, { source: 'page-button' })}
          >
            {tt(tourTitleKey(definition.id), tour.values)}
            {isFreshStatus(tour.statusOf(definition)) ? (
              <span className="sr-only">{t('tour.newSuffix')}</span>
            ) : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
